import type { FC } from "react";
import { useContext, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { shortMonthAndYear } from "@similarweb/pro-logic-components";
import { Button, PlainTooltip } from "@similarweb/ui-components";

import { InfoIcon } from "pro-features/components/BoxTitle/src/BoxTitle";
import { NoPrintContainer } from "app/components/layout/NoPrintContainer";
import { useIndustryOverviewTableData } from "app/pages/industry-analysis/overview/components/common/IndustryOverviewTableData/useIndustryOverviewTableData";
import { IndustryOverviewContext } from "app/pages/industry-analysis/overview/components/IndustryOverviewContext/IndustryOverviewContext";
import { WithIndustryOverviewContextHOC } from "app/pages/industry-analysis/overview/components/IndustryOverviewContext/withIndustryOverviewContextHOC";
import { MarketDistributionBody } from "app/pages/industry-analysis/overview/components/MarketDistribution/MarketDistributionBody";
import { MarketDistributionNoData } from "app/pages/industry-analysis/overview/components/MarketDistribution/MarketDistributionNoData";
import {
    HeaderContainer,
    Wrapper,
    TitleStyle,
    TitleContainer,
    DurationContainer,
    IconStyled,
    FooterContainer,
    LoadersContainer,
    LeftSpinner,
    RightSpinner,
    LeftContainer,
    RightContainer,
    TextStyle,
} from "app/pages/industry-analysis/overview/components/MarketDistribution/MarketDistributionStyles";
import type { IMarketDistribution } from "app/pages/industry-analysis/overview/components/MarketDistribution/types";
import type { ITopWebsitesApiResponse } from "app/pages/industry-analysis/overview/components/TopWebsitesChart/TopWebsitesChart.types";
import { BasicDurations, DurationService } from "app/services/DurationService";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { useCategories } from "../../../categories-context/CategoriesContext";
import { navigateWithCategorySupport } from "../../../routes/navigationInterceptor";

import { useMarketDistributionData } from "./hooks/useMarketDistributionData";
import { allTrackers } from "app/services/track/track";

const DOMAINS_AMOUNT_THRESHOLD = 100;
const TOP_WEBSITES_DEFAULT_DURATION = "1m";
const topWebsitesEndpoint = "/widgetApi/TopSitesExtended/TopSitesExtended/Table";
const OVERRIDE_API_PARAMS = {
    duration: TOP_WEBSITES_DEFAULT_DURATION,
    webSource: DEVICES_TYPES_FOR_API.TOTAL,
};

const MarketDistribution: FC<IMarketDistribution> = ({ width = "100%" }) => {
    const { services } = useContext(IndustryOverviewContext);
    const { translate } = services;
    const { categories } = useCategories();
    const { params } = useCurrentStateAndParams();
    const { duration, country, webSource, category } = params;

    // Use primary category from categories context
    const primaryCategory = categories[0]?.category;

    // Get market distribution data using React Query hook
    const { data: marketDistributionData, isLoading } = useMarketDistributionData(
        primaryCategory ? [primaryCategory] : [],
        country,
        webSource,
    );

    const serverData = marketDistributionData[0];

    const widgetTitle = translate("market.analysis.industry.overview.market.distribution.title");
    const widgetTooltip = translate("market.analysis.industry.overview.market.distribution.tooltip");
    const buttonText = translate("market.analysis.industry.overview.market.distribution.explore.button");

    // marketDistributionData comes from the hook and may be undefined while loading

    const hasServerData =
        serverData &&
        serverData.Data &&
        typeof serverData.Data === "object" &&
        Object.keys(serverData.Data as object).length > 0;
    const durationServices = new DurationService();
    const fullDuration = durationServices.getDurationData(BasicDurations.LAST_MONTH);
    const formattedDuration = fullDuration.raw.to.format(shortMonthAndYear);
    const selectedCategory = primaryCategory?.forApi;

    const { data: dataTopWebsitesOnTotalTraffic, isLoading: isLoadingTopWebsitesOnTotalTraffic } =
        useIndustryOverviewTableData<ITopWebsitesApiResponse>({
            services,
            endpoint: topWebsitesEndpoint,
            overrideParams: OVERRIDE_API_PARAMS,
        });

    const totalCount = useMemo(() => dataTopWebsitesOnTotalTraffic?.TotalCount, [dataTopWebsitesOnTotalTraffic]);
    const shouldShowWebsitesCount = totalCount && hasServerData;
    const basedOnTextReplacement = { websitesCountInIndustry: totalCount };
    const basedOnText = translate(
        "market.analysis.industry.overview.market.distribution.based.on.text",
        basedOnTextReplacement,
    );

    const renderLoadingState = () => {
        return (
            <LoadersContainer>
                <LeftContainer isSpinner>
                    <LeftSpinner />
                </LeftContainer>
                <RightContainer isSpinner>
                    <RightSpinner />
                </RightContainer>
            </LoadersContainer>
        );
    };

    const handleOnButtonClick = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Exploreleaders");
        const toState = "digitalsuite_marketresearch_webmarketanalysis_mapping";
        const navigationParams = {
            params,
            category,
            country,
            webSource,
            duration,
            isWWW: "*",
        };
        void navigateWithCategorySupport(toState, navigationParams);
    };

    const renderContent = () => {
        const isLoadingState = isLoading || isLoadingTopWebsitesOnTotalTraffic;

        //if the chosen traffic source is Desktop or Mobile and the industry has less than 100 domains,
        // and on Total traffic the industry has more than 100 domains - display an empty state
        const isNoDataBasedOnWebsites =
            [DEVICES_TYPES_FOR_API.MOBILE, DEVICES_TYPES_FOR_API.DESKTOP].includes(webSource) &&
            totalCount < DOMAINS_AMOUNT_THRESHOLD &&
            dataTopWebsitesOnTotalTraffic &&
            dataTopWebsitesOnTotalTraffic.TotalCount > DOMAINS_AMOUNT_THRESHOLD;
        const isNoData = isNoDataBasedOnWebsites || !hasServerData;

        return (
            <>
                {isLoadingState ? renderLoadingState() : null}
                {!isLoadingState ? (
                    isNoData ? (
                        <MarketDistributionNoData />
                    ) : (
                        <>
                            <MarketDistributionBody
                                serverData={serverData}
                                industryForApi={selectedCategory}
                            />
                            <FooterContainer>
                                <Button
                                    type="flat"
                                    onClick={handleOnButtonClick}
                                >
                                    {buttonText}
                                </Button>
                            </FooterContainer>
                        </>
                    )
                ) : null}
            </>
        );
    };

    // Only hide widget when:
    // 1. Industry has too few domains (< 100) on Total traffic
    // 2. Industry is a custom category
    // 3. No data available AND not loading
    const shouldHideMarketDistribution =
        (dataTopWebsitesOnTotalTraffic && dataTopWebsitesOnTotalTraffic.TotalCount < DOMAINS_AMOUNT_THRESHOLD) ||
        primaryCategory?.isCustomCategory === true ||
        (primaryCategory && !hasServerData && !isLoading && !isLoadingTopWebsitesOnTotalTraffic);

    return shouldHideMarketDistribution ? null : (
        <Wrapper
            data-automation={"market-distribution-widget"}
            wrapperWidth={width}
        >
            <HeaderContainer>
                <TitleContainer>
                    <TitleStyle>{widgetTitle}</TitleStyle>
                    <NoPrintContainer>
                        <PlainTooltip
                            placement="top"
                            tooltipContent={widgetTooltip}
                        >
                            <span>
                                <InfoIcon iconName="info" />
                            </span>
                        </PlainTooltip>
                    </NoPrintContainer>
                </TitleContainer>
                <DurationContainer>
                    {shouldShowWebsitesCount ? <TextStyle>{basedOnText}</TextStyle> : null}
                    <IconStyled
                        iconName="daily-ranking"
                        size="xs"
                    />
                    <TextStyle>{formattedDuration}</TextStyle>
                </DurationContainer>
            </HeaderContainer>
            {renderContent()}
        </Wrapper>
    );
};

export const MarketDistributionWidget = WithIndustryOverviewContextHOC(MarketDistribution);
